import { useFeatureFlagEnabled } from "@posthog/react";
import { MARKETPLACE_FLAG_KEY } from "../lib/feature-flags";
import { previewHomeGate } from "../lib/pro-home-preview";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useLenderMarketplaceGate } from "./useLenderMarketplaceGate";

export type MarketplaceGate = "loading" | "off" | "on";

/**
 * The ONE derivation of the marketplace gate from flag + DEV + lender role,
 * without the preview-Home override. Exported only for `useMarketplaceEnabled`
 * and `useMarketplaceRoutesRegistered`, which must not follow `?home=flagoff`.
 */
export function useMarketplaceGateBase(): MarketplaceGate {
  const flag = useFeatureFlagEnabled(MARKETPLACE_FLAG_KEY);
  const lender = useLenderMarketplaceGate();
  // A loan officer additionally needs `lender_marketplace_enabled`; off or
  // still resolving it withholds marketplace (zero marketplace requests).
  if (lender === "off") return "off";
  if (import.meta.env.DEV) return "on";
  if (flag === undefined) return "loading";
  if (flag !== true) return "off";
  return lender === "loading" ? "loading" : "on";
}

/**
 * Tri-state gate for the surfaces that must tell "flag still resolving" apart
 * from "flag off" (Home and the header badge): collapsing both to `false`
 * would flash the paused state at a pro whose flag is simply not loaded yet.
 * `useMarketplaceEnabled` is the boolean view of the same derivation.
 */
export function useMarketplaceGate(): MarketplaceGate {
  const gate = useMarketplaceGateBase();
  // Preview Home can simulate the gate OFF (`?home=flagoff`); otherwise preview
  // follows the DEV rule.
  if (IS_PREVIEW && previewHomeGate() === "off") return "off";
  return gate;
}
